/**
 * Simple Clerk-Only Authentication Service
 * No backend linking - just use Clerk directly
 */
import { useAuth, useUser, useClerk } from '@clerk/nextjs'
import { useEffect, useState, useCallback } from 'react'
import { setClerkTokenGetter } from './api'

export interface SimpleUser {
  id: string
  email: string
  fullName: string
  firstName?: string
  lastName?: string
  imageUrl?: string
  isActive: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  videoCount: number
  videoLimit: number
  storageUsedBytes: number
  storageLimitBytes: number
  subscriptionTier: string
}

interface AdminStatus {
  isAdmin: boolean
  isSuperAdmin: boolean
}

interface UseSimpleAuthReturn {
  // User state
  user: SimpleUser | null
  isLoaded: boolean
  isSignedIn: boolean
  isLoading: boolean
  isReady: boolean
  /** Clerk JS never finished loading (often: production keys + localhost not allowed in Clerk) */
  clerkLoadTimedOut: boolean
  /** Signed in but useUser() stayed unloaded; recovered via /api/auth/session-profile if possible */
  recoveredProfileViaServer: boolean

  // Actions
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

function simpleUserFromSessionPayload(b: Record<string, unknown>, imageUrl = ''): SimpleUser {
  return {
    id: String(b.id ?? ''),
    email: String(b.email ?? ''),
    fullName: String(b.full_name ?? ''),
    firstName: '',
    lastName: '',
    imageUrl,
    isActive: true,
    isAdmin: Boolean(b.is_admin),
    isSuperAdmin: Boolean(b.is_super_admin),
    videoCount: Number(b.video_count ?? 0),
    videoLimit: Number(b.video_limit ?? 10),
    storageUsedBytes: Number(b.storage_used_bytes ?? 0),
    storageLimitBytes: Number(b.storage_limit_bytes ?? 1024 * 1024 * 1024),
    subscriptionTier: String(b.subscription_tier ?? 'free'),
  }
}

export const useSimpleAuth = (): UseSimpleAuthReturn => {
  const { isLoaded: clerkLoaded, isSignedIn, getToken, userId } = useAuth()
  const { user: clerkUser, isLoaded: clerkUserLoaded } = useUser()
  const { signOut: clerkSignOut } = useClerk()

  const [user, setUser] = useState<SimpleUser | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [clerkLoadTimedOut, setClerkLoadTimedOut] = useState(false)
  const [userResourceTimedOut, setUserResourceTimedOut] = useState(false)
  const [recoveredProfileViaServer, setRecoveredProfileViaServer] = useState(false)

  // Set up the token getter for API calls
  useEffect(() => {
    if (getToken) {
      setClerkTokenGetter(getToken)
    }
  }, [getToken])

  useEffect(() => {
    if (clerkLoaded) {
      setClerkLoadTimedOut(false)
      return
    }
    const t = window.setTimeout(() => setClerkLoadTimedOut(true), 20_000)
    return () => window.clearTimeout(t)
  }, [clerkLoaded])

  useEffect(() => {
    if (!clerkLoaded || !isSignedIn || clerkUserLoaded) {
      setUserResourceTimedOut(false)
      return
    }
    const t = window.setTimeout(() => setUserResourceTimedOut(true), 10_000)
    return () => window.clearTimeout(t)
  }, [clerkLoaded, isSignedIn, clerkUserLoaded])

  // Check admin status from backend
  // The email argument is kept for call-site compatibility but no longer sent —
  // the route derives identity from the Clerk session, so it can't be used to
  // probe other people's addresses.
  const checkAdminStatus = useCallback(async (_email: string): Promise<AdminStatus> => {
    try {
      const response = await fetch('/api/auth/check-admin-status')
      if (response.ok) {
        const result = await response.json()
        return result
      }
    } catch (error) {
      // Silently fail and use defaults
    }
    return { isAdmin: false, isSuperAdmin: false }
  }, [])

  // Ensure user exists in backend and get their data
  const ensureUserInBackend = useCallback(async (clerkUser: any) => {
    try {
      // link-clerk-user, NOT the old ensure-clerk-user: that route was public
      // and took its identity from this body, so anyone could POST a victim's
      // email with their own clerk_id and take the account over. link-clerk-user
      // derives identity from the Clerk session and returns the same shape.
      const response = await fetch('/api/auth/link-clerk-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clerk_id: clerkUser.id,
          email: clerkUser.emailAddresses[0]?.emailAddress || '',
          full_name: clerkUser.fullName || ''
        })
      })

      if (response.ok) {
        const userData = await response.json()
        return userData
      }
    } catch (error) {
      // Silently fail - will use fallback data
    }
    return null
  }, [])

  // Convert Clerk user to our SimpleUser format
  const convertClerkUser = useCallback(async (clerkUser: any): Promise<SimpleUser> => {
    const email = clerkUser.emailAddresses[0]?.emailAddress || ''
    
    try {
      // First ensure user exists in backend (with timeout to prevent hanging)
      const backendUser = await Promise.race([
        ensureUserInBackend(clerkUser),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)) // 5 second timeout
      ])
      
      if (backendUser) {
        return {
          id: backendUser.id,
          email: email,
          fullName: backendUser.full_name || clerkUser.fullName || '',
          firstName: clerkUser.firstName || '',
          lastName: clerkUser.lastName || '',
          imageUrl: clerkUser.imageUrl || '',
          isActive: true,
          isAdmin: backendUser.is_admin || false,
          isSuperAdmin: backendUser.is_super_admin || false,
          videoCount: backendUser.video_count || 0,
          videoLimit: backendUser.video_limit || 10,
          storageUsedBytes: backendUser.storage_used_bytes || 0,
          storageLimitBytes: backendUser.storage_limit_bytes || (1024 * 1024 * 1024), // 1GB default
          subscriptionTier: backendUser.subscription_tier || 'free'
        }
      }
    } catch (error) {
      // Error already handled, using fallback
    }
    
    // Fallback: use Clerk user data only (fast, no blocking)
    return {
      id: clerkUser.id,
      email: email,
      fullName: clerkUser.fullName || '',
      firstName: clerkUser.firstName || '',
      lastName: clerkUser.lastName || '',
      imageUrl: clerkUser.imageUrl || '',
      isActive: true,
      isAdmin: false, // Default to false, will be updated later if needed
      isSuperAdmin: false, // Default to false
      videoCount: 0,
      videoLimit: 10,
      storageUsedBytes: 0,
      storageLimitBytes: 1024 * 1024 * 1024,
      subscriptionTier: 'free'
    }
  }, [ensureUserInBackend])

  // Track if we've already attempted authentication to prevent loops
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false)
  
  // Main authentication logic
  const authenticateUser = useCallback(async () => {
    // Don't show loading until Clerk is ready
    if (!clerkLoaded) {
      setIsLoading(false)
      setIsReady(false)
      return
    }

    // Not signed in — nothing to hydrate
    if (!isSignedIn) {
      setUser(null)
      setIsLoading(false)
      setIsReady(true)
      setHasAttemptedAuth(false)
      setRecoveredProfileViaServer(false)
      return
    }

    // Already have a profile while useUser() is still stuck (server recovery path)
    if (user && hasAttemptedAuth && !clerkUserLoaded) {
      setIsLoading(false)
      setIsReady(true)
      return
    }

    // Signed in but Clerk user resource not ready yet (common: production keys + localhost)
    if (!clerkUserLoaded) {
      if (!userResourceTimedOut) {
        setIsLoading(true)
        setIsReady(false)
        return
      }

      if (!userId) {
        setIsLoading(false)
        setIsReady(true)
        return
      }

      setIsLoading(true)
      setIsReady(false)
      try {
        const res = await fetch('/api/auth/session-profile', { credentials: 'same-origin' })
        if (res.ok) {
          const b = (await res.json()) as Record<string, unknown>
          setUser(simpleUserFromSessionPayload(b))
          setHasAttemptedAuth(true)
          setRecoveredProfileViaServer(true)
        } else {
          console.warn('[auth-simple] session-profile failed', res.status)
          setUser({
            id: userId,
            email: '',
            fullName: '',
            firstName: '',
            lastName: '',
            imageUrl: '',
            isActive: true,
            isAdmin: false,
            isSuperAdmin: false,
            videoCount: 0,
            videoLimit: 10,
            storageUsedBytes: 0,
            storageLimitBytes: 1024 * 1024 * 1024,
            subscriptionTier: 'free',
          })
          setHasAttemptedAuth(true)
        }
      } catch (e) {
        console.error('[auth-simple] session-profile', e)
        setUser({
          id: userId,
          email: '',
          fullName: '',
          firstName: '',
          lastName: '',
          imageUrl: '',
          isActive: true,
          isAdmin: false,
          isSuperAdmin: false,
          videoCount: 0,
          videoLimit: 10,
          storageUsedBytes: 0,
          storageLimitBytes: 1024 * 1024 * 1024,
          subscriptionTier: 'free',
        })
        setHasAttemptedAuth(true)
      } finally {
        setIsLoading(false)
        setIsReady(true)
      }
      return
    }

    if (!clerkUser) {
      setUser(null)
      setIsLoading(false)
      setIsReady(true)
      setHasAttemptedAuth(false)
      return
    }

    // After server recovery, merge Clerk names/avatar once useUser() catches up
    if (user && hasAttemptedAuth && clerkUser && recoveredProfileViaServer) {
      setIsLoading(true)
      try {
        const simpleUser = await convertClerkUser(clerkUser)
        setUser(simpleUser)
        setRecoveredProfileViaServer(false)
      } catch (error) {
        console.error('❌ Authentication error (merge Clerk profile):', error)
        setRecoveredProfileViaServer(false)
      } finally {
        setIsLoading(false)
        setIsReady(true)
      }
      return
    }

    if (user && hasAttemptedAuth) {
      setIsLoading(false)
      setIsReady(true)
      return
    }

    setIsLoading(true)
    setHasAttemptedAuth(true)
    
    try {
      const simpleUser = await convertClerkUser(clerkUser)
      setUser(simpleUser)
    } catch (error) {
      console.error('❌ Authentication error:', error)
      // Never leave signed-in sessions without a profile — login would spin forever
      setUser({
        id: clerkUser.id,
        email: clerkUser.emailAddresses[0]?.emailAddress || '',
        fullName: clerkUser.fullName || '',
        firstName: clerkUser.firstName || '',
        lastName: clerkUser.lastName || '',
        imageUrl: clerkUser.imageUrl || '',
        isActive: true,
        isAdmin: false,
        isSuperAdmin: false,
        videoCount: 0,
        videoLimit: 10,
        storageUsedBytes: 0,
        storageLimitBytes: 1024 * 1024 * 1024,
        subscriptionTier: 'free',
      })
    } finally {
      setIsLoading(false)
      setIsReady(true)
    }
  }, [
    clerkLoaded,
    isSignedIn,
    clerkUserLoaded,
    clerkUser,
    convertClerkUser,
    user,
    hasAttemptedAuth,
    userResourceTimedOut,
    userId,
    recoveredProfileViaServer,
  ])

  // Run authentication when dependencies change
  useEffect(() => {
    void authenticateUser()
    // authenticateUser omitted on purpose: it changes when `user` updates; these deps are the triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger list
  }, [clerkLoaded, isSignedIn, clerkUserLoaded, clerkUser?.id, userResourceTimedOut])

  const signOut = useCallback(async () => {
    try {
      // Clear local state immediately for smooth transition
      setUser(null)
      setIsLoading(false)
      setIsReady(false)
      setRecoveredProfileViaServer(false)

      // Sign out from Clerk
      if (clerkSignOut) {
        await clerkSignOut()
      }
      
      // Use router for smoother navigation instead of window.location
      window.location.href = '/login'
    } catch (error) {
      console.error('❌ Sign out error:', error)
      // Ensure we redirect even if there's an error
      window.location.href = '/login'
    }
  }, [clerkSignOut])

  const refreshUser = useCallback(async () => {
    if (clerkUser) {
      const simpleUser = await convertClerkUser(clerkUser)
      setUser(simpleUser)
    }
  }, [clerkUser, convertClerkUser])

  return {
    user,
    isLoaded: clerkLoaded,
    isSignedIn: isSignedIn || false,
    isLoading,
    isReady,
    clerkLoadTimedOut,
    recoveredProfileViaServer,
    signOut,
    refreshUser,
  }
}

// Export for backward compatibility
export const authService = {
  async getMe(): Promise<SimpleUser> {
    throw new Error('Use useSimpleAuth hook instead')
  },
  
  isAuthenticated(): boolean {
    return false // Always use the hook
  },
  
  logout() {
    window.location.href = '/login'
  }
}
